/**
 * Rolling vs fixed date options for saved stats filter presets.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';

import {
  getStatsDateRangeLabel,
  type StatsDateRangePreset,
} from '@/components/date-range';

interface SaveFilterDateRangeSectionProps {
  inferredRangePreset: StatsDateRangePreset;
  includeRolling: boolean;
  onIncludeRollingChange: (v: boolean) => void;
  includeFixed: boolean;
  onIncludeFixedChange: (v: boolean) => void;
  untilToday: boolean;
  onUntilTodayChange: (v: boolean) => void;
  previewFrom: string;
  previewToFixed: string;
  todayDmy: string;
}

export const SaveFilterDateRangeSection: FC<SaveFilterDateRangeSectionProps> = ({
  inferredRangePreset,
  includeRolling,
  onIncludeRollingChange,
  includeFixed,
  onIncludeFixedChange,
  untilToday,
  onUntilTodayChange,
  previewFrom,
  previewToFixed,
  todayDmy,
}) => {
  const rollingOk = inferredRangePreset !== 'custom';
  const rollingLabel = getStatsDateRangeLabel(inferredRangePreset);

  return (
    <div className="mt-4 rounded-md border border-border/60 bg-muted/20 px-3 py-2.5">
      <label
        className={`flex cursor-pointer items-start gap-2 ${!rollingOk ? 'opacity-60' : ''}`}
      >
        <input
          type="checkbox"
          checked={includeRolling}
          disabled={!rollingOk}
          onChange={(e) => onIncludeRollingChange(e.target.checked)}
          className="mt-0.5 h-3.5 w-3.5 rounded border-border disabled:cursor-not-allowed"
          data-testid="save-preset-rolling-date-range"
        />
        <span className="text-[11px] leading-snug text-foreground">
          <span className="font-medium">Save rolling date range</span>
          <span className="mt-0.5 block text-muted-foreground">
            Stores “{rollingLabel}” — each time you load this preset, the range is recalculated from
            the current day (not frozen calendar dates).
          </span>
        </span>
      </label>
      {!rollingOk ? (
        <p className="mt-2 pl-5 text-[11px] text-muted-foreground">
          Custom ranges cannot be saved as rolling; use fixed dates below or pick a preset from the
          date dropdown first.
        </p>
      ) : null}

      <label className="mt-3 flex cursor-pointer items-start gap-2 border-t border-border/40 pt-3">
        <input
          type="checkbox"
          checked={includeFixed}
          onChange={(e) => onIncludeFixedChange(e.target.checked)}
          className="mt-0.5 h-3.5 w-3.5 rounded border-border"
          data-testid="save-preset-fixed-date-range"
        />
        <span className="text-[11px] leading-snug text-foreground">
          <span className="font-medium">Save fixed calendar dates</span>
          <span className="mt-0.5 block text-muted-foreground">
            Stores the exact DD/MM/YYYY range shown below (same dates when you load the preset
            later).
          </span>
        </span>
      </label>
      {includeFixed ? (
        <div className="mt-2 space-y-2 pl-5">
          <label className="flex cursor-pointer items-start gap-2">
            <input
              type="checkbox"
              checked={untilToday}
              onChange={(e) => onUntilTodayChange(e.target.checked)}
              className="mt-0.5 h-3.5 w-3.5 rounded border-border"
              data-testid="save-preset-until-today"
            />
            <span className="text-[11px] leading-snug text-foreground">
              <span className="font-medium">End date always today</span>
              <span className="mt-0.5 block text-muted-foreground">
                Start date stays fixed; end date becomes the current day each time you load the
                preset.
              </span>
            </span>
          </label>
          <p
            className="text-[11px] tabular-nums text-muted-foreground"
            data-testid="save-preset-date-preview"
          >
            {untilToday
              ? `${previewFrom} → today (${todayDmy})`
              : `${previewFrom} → ${previewToFixed}`}
          </p>
        </div>
      ) : null}
    </div>
  );
};
